import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-[32px] border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-all duration-200 ease-in-out overflow-hidden transform-gpu",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90 ",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 ",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground hover:border-accent-foreground/20 ",
        main: "border-transparent bg-blue-50 text-blue-800 hover:bg-blue-100 ",
        additional:
          "border-transparent bg-green-50 text-green-800 hover:bg-green-100 ",
        excluded: "border-transparent bg-red-50 text-red-800 hover:bg-red-100 ",
        source:
          "border-neutral-200 bg-transparent text-neutral-800 text-xs font-medium leading-none !py-[12px] !px-[12px]  [a&]:hover:bg-primary/90 min-w-[32px] h-[22px] ",
        plan: "border-transparent bg-brand-500 text-admin-bg !py-0.5 !px-2 !text-[10px] font-semibold leading-none !rounded-[32px] h-[18px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  onRemove?: () => void;
}

function Badge({
  className,
  variant,
  onRemove,
  children,
  asChild = false,
  ...props
}: BadgeProps & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {children}
      {onRemove && (
        <button
          onClick={onRemove}
          className="ml-1 flex cursor-pointer items-center justify-center rounded-full transition-transform duration-150 ease-in-out outline-none"
        >
          <span
            className={cn(
              "flex h-[14px] w-[14px] items-center justify-center rounded-full transition-colors duration-200 hover:opacity-80",
              {
                main: "bg-blue-100 hover:bg-blue-200",
                additional: "bg-green-100 hover:bg-green-200",
                excluded: "bg-red-100 hover:bg-red-200",
              }[variant as "main" | "additional" | "excluded"] ??
                "bg-neutral-200 hover:bg-neutral-300",
            )}
          >
            <i className="ri-close-line text-[14px] text-neutral-800"></i>
          </span>
          <span className="sr-only">Remove</span>
        </button>
      )}
    </Comp>
  );
}

export { Badge };
